import { ChangeDetectorRef, Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { BaseWidgetComponent } from '../base-widget/base-widget.component';
import { HttpClient } from '@angular/common/http';
import { ConfigService } from 'src/app/services/config.service';
import { ClaudeAuthWizardService, AuthStatus } from 'src/app/services/claude-auth-wizard.service';
import { CodexAuthWizardService, CodexAuthStatus } from 'src/app/services/codex-auth-wizard.service';
import { GeminiAuthWizardService, GeminiAuthStatus } from 'src/app/services/gemini-auth-wizard.service';
import { AntigravityAuthWizardService, AntigravityAuthStatus } from 'src/app/services/antigravity-auth-wizard.service';
import { firstValueFrom, Subscription } from 'rxjs';

export interface BackendInfo {
  id: string;
  name: string;
  display_name: string;
  description: string;
  enabled: boolean;
  healthy: boolean;
  configured: boolean;
  config_type: 'api_key' | 'cli_auth' | 'local' | 'none';
  config_instructions?: string;
  config_url?: string;
  install_command?: string;
  models: string[];
}

export interface TokenDetails {
  scopes?: string[];
  expires_at?: number;
  expires_in?: string;
}

export interface BackendStatus {
  id: string;
  registered: boolean;
  enabled: boolean;
  healthy: boolean;
  configured: boolean;
  auth_status: 'installed' | 'authenticated' | 'not_installed' | 'not_authenticated' | 'token_expired' | 'error' | null;
  error: string | null;
  warning?: string | null;
  token_details?: TokenDetails;
}

@Component({
    selector: 'app-backend-detail-widget',
    templateUrl: './backend-detail-widget.component.html',
    styleUrls: ['../base-widget/base-widget.component.scss', './backend-detail-widget.component.scss'],
    standalone: false
})
export class BackendDetailWidgetComponent extends BaseWidgetComponent implements OnInit, OnDestroy {
  @Input() backendId: string = 'claude-cli';
  @Input() credentialsId: string | null = null;
  @Output() onOpenTerminal = new EventEmitter<void>();

  public backend: BackendInfo | null = null;
  public backendStatus: BackendStatus | null = null;
  public isLoading = true;
  public errorMessage: string | null = null;

  // Configuration form
  public apiKey = '';
  public hostUrl = '';
  public isSaving = false;
  public isCheckingStatus = false;

  // CLI auth wizard state (works for both Claude and Codex)
  private _isAuthenticating = false;
  public get isAuthenticating(): boolean {
    return this._isAuthenticating;
  }
  public set isAuthenticating(value: boolean) {
    console.log('isAuthenticating SET TO:', value, new Error().stack);
    this._isAuthenticating = value;
  }
  public authWizardStatus: AuthStatus | CodexAuthStatus | AntigravityAuthStatus | null = null;
  public authWizardError: string | null = null;
  public authCode = '';  // For Claude: OAuth code to paste back
  public isSubmittingCode = false;
  private authStatusSub: Subscription | null = null;
  private authErrorSub: Subscription | null = null;

  // Codex-specific: device auth URL and code (displayed to user, not pasted)
  public codexDeviceUrl: string | null = null;
  public codexDeviceCode: string | null = null;
  private codexAuthStatusSub: Subscription | null = null;
  private codexAuthErrorSub: Subscription | null = null;

  // Gemini-specific
  private geminiAuthStatusSub: Subscription | null = null;
  private geminiAuthErrorSub: Subscription | null = null;

  // Antigravity-specific
  private antigravityAuthStatusSub: Subscription | null = null;
  private antigravityAuthErrorSub: Subscription | null = null;

  constructor(
    private http: HttpClient,
    private claudeAuthWizard: ClaudeAuthWizardService,
    private codexAuthWizard: CodexAuthWizardService,
    private geminiAuthWizard: GeminiAuthWizardService,
    private antigravityAuthWizard: AntigravityAuthWizardService,
    private _cdr: ChangeDetectorRef
  ) {
    super();
    (window as any).authComp = this;
    console.log('BackendDetailWidget assigned to window.authComp');
  }

  /**
   * Check if current backend is ChatGPT/Codex
   */
  get isChatGPTBackend(): boolean {
    return this.backendId === 'chatgpt';
  }

  /**
   * Check if current backend is Claude CLI
   */
  get isClaudeBackend(): boolean {
    return this.backendId === 'claude-cli' || this.backendId === 'claude-interactive';
  }

  /**
   * Check if current backend is Gemini
   */
  get isGeminiBackend(): boolean {
    return this.backendId === 'gemini';
  }

  /**
   * Check if current backend is Antigravity
   */
  get isAntigravityBackend(): boolean {
    return this.backendId === 'antigravity';
  }

  async ngOnInit(): Promise<void> {
    await this.loadBackend();

    // Subscribe to Claude auth wizard status updates
    this.authStatusSub = this.claudeAuthWizard.authStatus$.subscribe(status => {
      if (this.isClaudeBackend) {
        this.authWizardStatus = status;
        this._cdr.detectChanges();
      }
    });

    this.authErrorSub = this.claudeAuthWizard.error$.subscribe(error => {
      if (this.isClaudeBackend) {
        this.authWizardError = error;
      }
    });

    // Subscribe to Codex auth wizard status updates
    this.codexAuthStatusSub = this.codexAuthWizard.authStatus$.subscribe(status => {
      if (this.isChatGPTBackend) {
        this.authWizardStatus = status;
        // Auto-complete when authenticated
        if (status === 'authenticated') {
          this.isAuthenticating = false;
          this.checkBackendStatus();
        }
        this._cdr.detectChanges();
      }
    });

    this.codexAuthErrorSub = this.codexAuthWizard.error$.subscribe(error => {
      if (this.isChatGPTBackend) {
        this.authWizardError = error;
      }
    });

    // Subscribe to Gemini auth wizard status updates
    this.geminiAuthStatusSub = this.geminiAuthWizard.authStatus$.subscribe(status => {
      if (this.isGeminiBackend) {
        this.authWizardStatus = status;
        if (status === 'authenticated') {
          this.isAuthenticating = false;
          this.authCode = '';
          this.checkBackendStatus();
        }
        this._cdr.detectChanges();
      }
    });

    this.geminiAuthErrorSub = this.geminiAuthWizard.error$.subscribe(error => {
      if (this.isGeminiBackend) {
        this.authWizardError = error;
      }
    });

    // Subscribe to Antigravity auth wizard status updates
    this.antigravityAuthStatusSub = this.antigravityAuthWizard.authStatus$.subscribe(status => {
      if (this.isAntigravityBackend) {
        this.authWizardStatus = status;
        if (status === 'authenticated') {
          this.isAuthenticating = false;
          this.authCode = '';
          this.checkBackendStatus();
        }
        this._cdr.detectChanges();
      }
    });

    this.antigravityAuthErrorSub = this.antigravityAuthWizard.error$.subscribe(error => {
      if (this.isAntigravityBackend) {
        this.authWizardError = error;
      }
    });
  }

  ngOnDestroy(): void {
    this.authStatusSub?.unsubscribe();
    this.authErrorSub?.unsubscribe();
    this.codexAuthStatusSub?.unsubscribe();
    this.codexAuthErrorSub?.unsubscribe();
    this.geminiAuthStatusSub?.unsubscribe();
    this.geminiAuthErrorSub?.unsubscribe();
    this.antigravityAuthStatusSub?.unsubscribe();
    this.antigravityAuthErrorSub?.unsubscribe();
    this.claudeAuthWizard.reset();
    this.codexAuthWizard.reset();
    this.geminiAuthWizard.reset();
    this.antigravityAuthWizard.reset();
    super.ngOnDestroy();
  }

  async loadBackend(): Promise<void> {
    this.isLoading = true;
    this.errorMessage = null;

    try {
      const response = await firstValueFrom(
        this.http.get<BackendInfo[]>(`${ConfigService.muxUrl}/backends/info`)
      );
      this.backend = response.find(b => b.id === this.backendId) || null;

      if (!this.backend) {
        this.errorMessage = `Backend "${this.backendId}" not found`;
      } else {
        await this.checkBackendStatus();
      }
    } catch (error) {
      console.error('Failed to load backend:', error);
      this.errorMessage = 'Failed to load backend';
    } finally {
      this.isLoading = false;
      // Trigger change detection for dynamic component in OnPush parent
      this._cdr.detectChanges();
    }
  }

  async checkBackendStatus(): Promise<void> {
    if (!this.backend) return;

    this.isCheckingStatus = true;
    try {
      const params: any = {};
      if (this.credentialsId) {
        params.credentials_id = this.credentialsId;
      }
      const response = await firstValueFrom(
        this.http.get<BackendStatus>(`${ConfigService.muxUrl}/backends/${this.backend.id}/status`, { params })
      );
      this.backendStatus = response;
    } catch (error) {
      console.error('Failed to check backend status:', error);
    } finally {
      this.isCheckingStatus = false;
    }
  }

  async saveApiKeyConfig(): Promise<void> {
    if (!this.backend || !this.apiKey.trim()) return;

    this.isSaving = true;
    this.errorMessage = null;

    try {
      await firstValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/${this.backend.id}/configure`, {
          api_key: this.apiKey.trim()
        })
      );
      await this.checkBackendStatus();
    } catch (error: any) {
      console.error('Failed to configure backend:', error);
      this.errorMessage = error?.error?.detail || 'Failed to save configuration';
    } finally {
      this.isSaving = false;
    }
  }

  async saveHostConfig(): Promise<void> {
    if (!this.backend || !this.hostUrl.trim()) return;

    this.isSaving = true;
    this.errorMessage = null;

    try {
      await firstValueFrom(
        this.http.post(`${ConfigService.muxUrl}/backends/${this.backend.id}/configure`, {
          host: this.hostUrl.trim()
        })
      );
      await this.checkBackendStatus();
    } catch (error: any) {
      console.error('Failed to configure backend:', error);
      this.errorMessage = error?.error?.detail || 'Failed to save configuration';
    } finally {
      this.isSaving = false;
    }
  }

  openTerminal(): void {
    this.onOpenTerminal.emit();
  }

  /**
   * Start authentication - routes to appropriate wizard based on backend
   */
  async startAuth(): Promise<void> {
    if (this.isChatGPTBackend) {
      await this.startCodexAuth();
    } else if (this.isGeminiBackend) {
      await this.startGeminiAuth();
    } else if (this.isAntigravityBackend) {
      await this.startAntigravityAuth();
    } else {
      await this.startClaudeAuth();
    }
  }

  async startClaudeAuth(): Promise<void> {
    this.isAuthenticating = true;
    this.authWizardError = null;

    try {
      const session = await this.claudeAuthWizard.startAuth(this.credentialsId || undefined);

      if (session.oauth_url) {
        window.open(session.oauth_url, '_blank');
        this.claudeAuthWizard.startPolling();
      } else if (session.status === 'authenticated') {
        this.isAuthenticating = false;
        await this.checkBackendStatus();
      } else if (session.error) {
        this.authWizardError = session.error;
        this.isAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start Claude auth:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAuthenticating = false;
    }
  }

  async startCodexAuth(): Promise<void> {
    this.isAuthenticating = true;
    this.authWizardError = null;
    this.codexDeviceUrl = null;
    this.codexDeviceCode = null;

    try {
      // If already authenticated, clear credentials first so start_auth doesn't short-circuit
      if (this.backendStatus?.auth_status === 'authenticated') {
        await this.codexAuthWizard.deauth();
      }

      const session = await this.codexAuthWizard.startAuth();

      if (session.device_url && session.device_code) {
        this.codexDeviceUrl = session.device_url;
        this.codexDeviceCode = session.device_code;
        this.codexAuthWizard.startPolling();
      } else if (session.status === 'authenticated') {
        this.isAuthenticating = false;
        await this.checkBackendStatus();
      } else if (session.error) {
        this.authWizardError = session.error;
        this.isAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start Codex auth:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAuthenticating = false;
    }
  }

  async startGeminiAuth(): Promise<void> {
    this.isAuthenticating = true;
    this.authWizardError = null;

    try {
      const session = await this.geminiAuthWizard.startAuth();

      if (session.oauth_url) {
        window.open(session.oauth_url, '_blank');
      } else if (session.status === 'authenticated') {
        this.isAuthenticating = false;
        await this.checkBackendStatus();
      } else if (session.error) {
        this.authWizardError = session.error;
        this.isAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start Gemini auth:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAuthenticating = false;
    }
  }

  async startAntigravityAuth(): Promise<void> {
    this.isAuthenticating = true;
    this.authWizardError = null;

    try {
      const session = await this.antigravityAuthWizard.startAuth();

      if (session.oauth_url) {
        window.open(session.oauth_url, '_blank');
      } else if (session.status === 'authenticated') {
        this.isAuthenticating = false;
        await this.checkBackendStatus();
      } else if (session.error) {
        this.authWizardError = session.error;
        this.isAuthenticating = false;
      }
    } catch (error: any) {
      console.error('Failed to start Antigravity auth:', error);
      this.authWizardError = error?.error?.detail || error?.message || 'Failed to start authentication';
      this.isAuthenticating = false;
    }
  }

  /**
   * Cancel authentication - routes to appropriate wizard based on backend
   */
  async cancelAuth(): Promise<void> {
    if (this.isChatGPTBackend) {
      await this.codexAuthWizard.cancelAuth();
      this.codexDeviceUrl = null;
      this.codexDeviceCode = null;
    } else if (this.isGeminiBackend) {
      await this.geminiAuthWizard.cancelAuth();
      this.authCode = '';
    } else if (this.isAntigravityBackend) {
      await this.antigravityAuthWizard.cancelAuth();
      this.authCode = '';
    } else {
      await this.claudeAuthWizard.cancelAuth();
      this.authCode = '';
    }
    this.isAuthenticating = false;
  }

  async cancelClaudeAuth(): Promise<void> {
    await this.cancelAuth();
  }

  async submitAuthCode(): Promise<void> {
    if (!this.authCode.trim()) {
      return;
    }

    this.isSubmittingCode = true;
    this.authWizardError = null;
    this._cdr.detectChanges();

    try {
      let result: { success: boolean; message?: string; error?: string };

      if (this.isGeminiBackend) {
        result = await this.geminiAuthWizard.submitCode(this.authCode.trim());
      } else if (this.isAntigravityBackend) {
        result = await this.antigravityAuthWizard.submitCode(this.authCode.trim());
      } else {
        result = await this.claudeAuthWizard.submitCode(this.authCode.trim());
      }

      if (result.success) {
        this.isAuthenticating = false;
        this.authCode = '';
        await this.checkBackendStatus();
      } else {
        this.authWizardError = result.message || result.error || 'Failed to submit code';
      }
    } catch (error: any) {
      console.error('Failed to submit auth code:', error);
      this.authWizardError = error?.message || 'Failed to submit code';
    } finally {
      this.isSubmittingCode = false;
      this._cdr.detectChanges();
    }
  }

  /**
   * Open the device auth URL in a new tab (for Codex)
   */
  openDeviceAuthUrl(): void {
    if (this.codexDeviceUrl) {
      window.open(this.codexDeviceUrl, '_blank');
    }
  }

  /**
   * Copy device code to clipboard (for Codex)
   */
  async copyDeviceCode(): Promise<void> {
    if (this.codexDeviceCode) {
      try {
        await navigator.clipboard.writeText(this.codexDeviceCode);
      } catch (error) {
        console.error('Failed to copy device code:', error);
      }
    }
  }

  getAuthWizardStatusText(): string {
    switch (this.authWizardStatus) {
      case 'starting':
        return 'Starting authentication...';
      case 'waiting_code':
        return 'Getting device code...';
      case 'waiting':
        return this.isChatGPTBackend
          ? 'Enter the code in your browser to complete authentication...'
          : 'Complete authentication in your browser...';
      case 'authenticated':
        return 'Authenticated successfully!';
      case 'failed':
        return this.authWizardError || 'Authentication failed';
      case 'cancelled':
        return 'Authentication cancelled';
      case 'needs_intervention':
        return 'Authentication requires manual intervention';
      default:
        return '';
    }
  }

  getAuthStatusText(): string {
    if (!this.backendStatus) return 'Unknown';
    switch (this.backendStatus.auth_status) {
      case 'authenticated':
        if (this.backendStatus.warning) {
          return `⚠️ ${this.backendStatus.warning}`;
        }
        return '✅ Authenticated';
      case 'token_expired': return `❌ ${this.backendStatus.error || 'Token expired'}`;
      case 'installed': return '⚠️ Installed but not authenticated';
      case 'not_installed': return '❌ Not installed';
      case 'not_authenticated': return '⚠️ Not authenticated';
      case 'error': return `❌ Error: ${this.backendStatus.error}`;
      default: return 'Unknown';
    }
  }

  shouldShowAuthButton(): boolean {
    if (this.isAuthenticating) return false;
    if (this.isCheckingStatus && !this.backendStatus) return false;
    return true;
  }

  getAuthButtonLabel(): string {
    const providerName = (this.isGeminiBackend || this.isAntigravityBackend) ? 'Google' : this.isChatGPTBackend ? 'ChatGPT' : 'Claude';
    if (!this.backendStatus) return `Sign in with ${providerName}`;
    switch (this.backendStatus.auth_status) {
      case 'authenticated': return 'Re-authenticate';
      case 'token_expired': return 'Refresh Authentication';
      default: return `Sign in with ${providerName}`;
    }
  }

  openConfigUrl(): void {
    if (this.backend?.config_url) {
      window.open(this.backend.config_url, '_blank');
    }
  }

  get widgetTitle(): string {
    return this.backend?.display_name || this.backend?.name || 'Backend';
  }
}
